import { API_BASE_URL } from '../config'

const PROBE_TIMEOUT_MS = 8000
const PROBE_INTERVAL_MS = 15000

let reachable = true
let lastReachableAt = 0
let probe: Promise<boolean> | null = null
let probeTimer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

function setReachable(value: boolean) {
  if (reachable === value) return
  reachable = value
  notify()
}

function cancelProbe() {
  if (!probeTimer) return
  clearTimeout(probeTimer)
  probeTimer = null
}

function scheduleProbe() {
  if (probeTimer || !navigator.onLine) return
  probeTimer = setTimeout(() => {
    probeTimer = null
    void probeServer()
  }, PROBE_INTERVAL_MS)
}

export function markServerReachable(): void {
  lastReachableAt = performance.now()
  cancelProbe()
  setReachable(true)
}

// Note: a request that set off before the server last answered says nothing about the link now. On a link
// that has just come back, those stragglers fail late, and letting them count knocked the app offline again.
export function markServerUnreachable(requestStartedAt = performance.now()): void {
  if (requestStartedAt < lastReachableAt) return
  setReachable(false)
  scheduleProbe()
}

export function isServerReachable(): boolean {
  return reachable
}

export function isOnlineNow(): boolean {
  return navigator.onLine && reachable
}

export function subscribeToReachability(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function subscribeToOnlineStatus(listener: () => void): () => void {
  window.addEventListener('online', listener)
  window.addEventListener('offline', listener)
  const unsubscribe = subscribeToReachability(listener)
  return () => {
    window.removeEventListener('online', listener)
    window.removeEventListener('offline', listener)
    unsubscribe()
  }
}

// Note: any HTTP response proves the server can be reached, a 401 included, so the probe needs no token. The
// timezone endpoint is anonymous and reads only configuration.
export function probeServer(): Promise<boolean> {
  if (!navigator.onLine) {
    setReachable(false)
    return Promise.resolve(false)
  }

  if (!probe) {
    const startedAt = performance.now()
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)

    probe = fetch(`${API_BASE_URL}/Settings/timezone`, { cache: 'no-store', signal: controller.signal })
      .then(
        () => {
          markServerReachable()
          return true
        },
        () => {
          markServerUnreachable(startedAt)
          return false
        })
      .finally(() => {
        clearTimeout(timeout)
        probe = null
      })
  }

  return probe
}

// Note: the online event only says a network is attached, which on Android is also true of wifi with no route
// and of a SIM with no data, so the server is asked rather than the event believed.
window.addEventListener('online', () => { void probeServer() })
window.addEventListener('offline', () => {
  cancelProbe()
  setReachable(false)
})
window.addEventListener('pageshow', () => { void probeServer() })
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void probeServer()
})
